import { type InstallHealthStatus, type UpdateStatus } from '@electron/shared';
import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';

import { ForceUpdateModal } from '@/components/modals/ForceUpdateModal';
import { InstallRepairModal } from '@/components/modals/InstallRepairModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { ReleaseNotesModal } from '@/components/modals/ReleaseNotesModal';
import { electronApis } from '@/features/electron-api';
import { trackEvent } from '@/utils/analytics';
import { DOWNLOAD_URL } from '@/utils/deepLink';

interface UpdateState {
  isChecking: boolean;
  isDownloading: boolean;
  updateAvailable: boolean;
  downloadProgress: number | null;
  availableVersion: string | null;
  downloadedVersion: string | null;
  releaseNotes: string | null;
  currentVersion: string;
  error: string | null;
}

export class UpdateStore {
  state: UpdateState = {
    isChecking: false,
    isDownloading: false,
    updateAvailable: false,
    downloadProgress: null,
    availableVersion: null,
    downloadedVersion: null,
    releaseNotes: null,
    currentVersion: '',
    error: null,
  };

  userVersionSupported = true;
  installHealth: InstallHealthStatus | null = null;
  repairError: string | null = null;
  private reactionCleanups: Array<() => void> = [];

  constructor() {
    makeObservable(this, {
      state: observable,
      userVersionSupported: observable,
      installHealth: observable,
      repairError: observable,
      isUpdateAvailable: computed,
      isUpdateReady: computed,
      canDownloadUpdate: computed,
      shouldShowToast: computed,
      shouldForceUpdate: computed,
      handleUpdateStatus: action,
      setUserVersionSupported: action,
      installUpdate: action,
      downloadUpdate: action,
      handleInstallHealth: action,
      repairInstall: action,
      dismissInstallRepair: action,
      downloadAndQuit: action,
      quitApp: action,
    });
  }

  get isUpdateAvailable(): boolean {
    return (
      this.state.updateAvailable &&
      !!this.state.availableVersion &&
      this.state.availableVersion !== this.state.currentVersion
    );
  }

  get canDownloadUpdate(): boolean {
    return (
      this.isUpdateAvailable && !this.isUpdateReady && !this.state.isDownloading
    );
  }

  get isUpdateReady(): boolean {
    return (
      this.state.downloadedVersion !== null &&
      this.state.downloadedVersion === this.state.availableVersion
    );
  }

  get shouldShowToast(): boolean {
    return this.isUpdateReady && this.userVersionSupported;
  }

  get shouldForceUpdate(): boolean {
    return this.isUpdateReady && !this.userVersionSupported;
  }

  initReactions(): void {
    this.reactionCleanups.push(
      reaction(
        () => this.shouldForceUpdate,
        (shouldForce) => {
          if (shouldForce) {
            ModalsManager.show(
              ForceUpdateModal,
              { version: this.state.availableVersion ?? undefined },
              { unique: true }
            );
          }
        },
        { fireImmediately: true }
      )
    );

    this.reactionCleanups.push(
      reaction(
        () => this.installHealth?.shouldPrompt === true,
        (shouldPrompt) => {
          if (shouldPrompt) {
            ModalsManager.show(InstallRepairModal, {}, { unique: true });
          }
        },
        { fireImmediately: true }
      )
    );
  }

  cleanup(): void {
    this.reactionCleanups.forEach((cleanup) => cleanup());
    this.reactionCleanups = [];
    this.installHealth = null;
    this.repairError = null;
  }

  setUserVersionSupported(supported: boolean): void {
    this.userVersionSupported = supported;
  }

  handleUpdateStatus(status: UpdateStatus): void {
    // Clear stale download if version changed or was rolled back
    const shouldInvalidateDownload =
      this.state.downloadedVersion &&
      status.availableVersion &&
      (status.availableVersion !== this.state.downloadedVersion ||
        status.availableVersion === status.currentVersion);

    if (shouldInvalidateDownload) {
      this.state.downloadedVersion = null;
    }

    this.state.isChecking = status.isChecking;
    this.state.isDownloading = status.isDownloading;
    this.state.updateAvailable = status.updateAvailable;
    this.state.downloadProgress = status.downloadProgress?.percent ?? null;
    this.state.availableVersion = status.availableVersion ?? null;
    this.state.releaseNotes = status.releaseNotes ?? null;
    this.state.currentVersion = status.currentVersion;
    this.state.error = status.error ?? null;

    // Only mark as downloaded if there's actually a newer version
    if (
      status.isDownloaded &&
      status.availableVersion &&
      status.availableVersion !== status.currentVersion
    ) {
      this.state.downloadedVersion = status.availableVersion;
    }
  }

  installUpdate(): void {
    trackEvent('UPDATE_INSTALL_STARTED', undefined, true);
    electronApis.system.installUpdate();
  }

  async downloadUpdate(): Promise<void> {
    if (this.isUpdateReady || this.state.isDownloading) return;
    trackEvent('UPDATE_DOWNLOAD_STARTED', {
      version: this.state.availableVersion,
    });
    this.state.isDownloading = true;
    const result = await electronApis.system.downloadUpdate();
    if (!result.success) {
      runInAction(() => {
        this.state.isDownloading = false;
        this.state.error = result.error ?? 'Download failed';
      });
    }
  }

  // Catches up on a status main emitted before this renderer subscribed
  async syncUpdateStatus(): Promise<void> {
    this.handleUpdateStatus(await electronApis.system.getUpdateStatus());
  }

  showReleaseNotes(): void {
    ModalsManager.show(ReleaseNotesModal, {
      version: this.state.availableVersion ?? undefined,
      releaseNotes: this.state.releaseNotes ?? undefined,
    });
  }

  handleInstallHealth(health: InstallHealthStatus): void {
    this.installHealth = health;
    this.repairError = null;
  }

  // Catches up on the verdict main emitted before this renderer subscribed
  async syncInstallHealth(): Promise<void> {
    this.handleInstallHealth(await electronApis.system.getInstallHealth());
  }

  async repairInstall(): Promise<void> {
    trackEvent('INSTALL_REPAIR_ACCEPTED');
    const result = await electronApis.system.repairInstall();
    if (!result.success) {
      runInAction(() => {
        this.repairError = result.error ?? 'Repair failed';
      });
    }
  }

  dismissInstallRepair(): void {
    trackEvent('INSTALL_REPAIR_DISMISSED');
    electronApis.system.dismissInstallRepair();
  }

  async downloadAndQuit(): Promise<void> {
    trackEvent('INSTALL_REPAIR_DOWNLOAD_CLICKED');
    await electronApis.system.openExternal(DOWNLOAD_URL);
    void electronApis.system.quitApp('install_repair');
  }

  quitApp(): void {
    void electronApis.system.quitApp('install_repair');
  }
}

export const updateStore = new UpdateStore();
